import { useMemo, useState } from "react";
import Event from "./Event";

const MONTH_LABELS = [
    "Januari",
    "Februari",
    "Maart",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Augustus",
    "September",
    "Oktober",
    "November",
    "December",
];

function EventList({ projects }) {
    const [collapsedYears, setCollapsedYears] = useState([]);
    const [filteredOutMonths, setFilteredOutMonths] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const isFutureDate = (date) => {
        const [day, month, year] = date.split("-").map(Number);
        const today = new Date();
        return (
            year > today.getFullYear() ||
            (year === today.getFullYear() && month > today.getMonth() + 1) ||
            (year === today.getFullYear() &&
                month === today.getMonth() + 1 &&
                day >= today.getDate())
        );
    };

    const toggleMonth = (month) => {
        setFilteredOutMonths((prev) =>
            prev.includes(month)
                ? prev.filter((m) => m !== month)
                : [...prev, month],
        );
    };

    const projectsWithUpcomingShows = projects.filter(
        (project) => project.shows !== undefined,
    );

    const availableProjects = useMemo(() => {
        const titles = new Set();
        projectsWithUpcomingShows.forEach((project) => {
            project.shows.forEach((show) => {
                if (show.dates.some(isFutureDate)) {
                    titles.add(project.title);
                }
            });
        });
        return Array.from(titles);
    }, [projectsWithUpcomingShows]);

    const yearGroups = useMemo(() => {
        const groups = new Map();

        projectsWithUpcomingShows.forEach((project) => {
            project.shows.forEach((show) => {
                const futureDates = show.dates.filter(isFutureDate);
                if (
                    selectedProjects.length > 0 &&
                    !selectedProjects.includes(project.title)
                ) {
                    return;
                }
                futureDates.forEach((date) => {
                    const [, month, year] = date.split("-").map(Number);
                    if (!groups.has(year)) groups.set(year, new Map());
                    const months = groups.get(year);
                    if (!months.has(month)) months.set(month, []);
                    months.get(month).push({ project, show, date });
                });
            });
        });

        return new Map([...groups.entries()].sort((a, b) => a[0] - b[0]));
    }, [projectsWithUpcomingShows, selectedProjects]);

    const hasFutureEvents = yearGroups.size > 0;

    if (!hasFutureEvents) {
        return (
            <div className="event-container">
                <h2 className="no-events-heading">Geen event op dit moment</h2>
            </div>
        );
    }

    return (
        <div className="event-container">
            <div className="event-filter-chips">
                {availableProjects.map((title) => {
                    const isActive = selectedProjects.includes(title);
                    return (
                        <button
                            key={title}
                            className={`chip ${isActive ? "active" : ""}`}
                            onClick={() =>
                                setSelectedProjects((prev) =>
                                    prev.includes(title)
                                        ? prev.filter((t) => t !== title)
                                        : [...prev, title],
                                )
                            }
                        >
                            {title}
                        </button>
                    );
                })}
                {selectedProjects.length > 0 && (
                    <button
                        className="chip reset"
                        onClick={() => setSelectedProjects([])}
                    >
                        Reset filters
                    </button>
                )}
            </div>
            <div className="event-list">
                {[...yearGroups.entries()].map(([year, months]) => (
                    <div
                        className="month-block"
                        key={year}
                    >
                        <button
                            className="month-filter-btn"
                            aria-expanded={!collapsedYears.includes(year)}
                            onClick={() =>
                                setCollapsedYears((prev) =>
                                    prev.includes(year)
                                        ? prev.filter((value) => value !== year)
                                        : [...prev, year],
                                )
                            }
                        >
                            <span>{year}</span>
                            <span
                                className={`month-filter-chevron ${
                                    collapsedYears.includes(year)
                                        ? "collapsed"
                                        : "expanded"
                                }`}
                            >
                                <ion-icon name="chevron-down-outline"></ion-icon>
                            </span>
                        </button>
                        {!collapsedYears.includes(year) &&
                            [...months.entries()]
                                .sort((a, b) => a[0] - b[0])
                                .map(([month, entries]) => {
                                    const monthKey = `${year}-${month}`;
                                    const byLocation = entries.reduce(
                                        (acc, entry) => {
                                            const key = `${entry.show.location}-${entry.show.city}-${entry.project.title}`;
                                            if (!acc.has(key)) {
                                                acc.set(key, {
                                                    ...entry,
                                                    dates: [],
                                                });
                                            }
                                            acc.get(key).dates.push(entry.date);
                                            return acc;
                                        },
                                        new Map(),
                                    );

                                    return (
                                        <div
                                            className="month-block"
                                            key={monthKey}
                                        >
                                            <button
                                                className="month-filter-btn"
                                                aria-expanded={
                                                    !filteredOutMonths.includes(
                                                        monthKey,
                                                    )
                                                }
                                                onClick={() =>
                                                    toggleMonth(monthKey)
                                                }
                                            >
                                                <span className="month-filter-label">
                                                    {MONTH_LABELS[month - 1]}
                                                </span>
                                                <span
                                                    className={`month-filter-chevron ${
                                                        filteredOutMonths.includes(
                                                            monthKey,
                                                        )
                                                            ? "collapsed"
                                                            : "expanded"
                                                    }`}
                                                >
                                                    <ion-icon name="chevron-down-outline"></ion-icon>
                                                </span>
                                            </button>

                                            {!filteredOutMonths.includes(
                                                monthKey,
                                            ) &&
                                                [...byLocation.values()].map(
                                                    (entry) => {
                                                        const sortedDates =
                                                            entry.dates.sort(
                                                                (a, b) => {
                                                                    const [
                                                                        da,
                                                                        ma,
                                                                        ya,
                                                                    ] = a
                                                                        .split(
                                                                            "-",
                                                                        )
                                                                        .map(
                                                                            Number,
                                                                        );
                                                                    const [
                                                                        db,
                                                                        mb,
                                                                        yb,
                                                                    ] = b
                                                                        .split(
                                                                            "-",
                                                                        )
                                                                        .map(
                                                                            Number,
                                                                        );
                                                                    return (
                                                                        new Date(
                                                                            ya,
                                                                            ma -
                                                                                1,
                                                                            da,
                                                                        ) -
                                                                        new Date(
                                                                            yb,
                                                                            mb -
                                                                                1,
                                                                            db,
                                                                        )
                                                                    );
                                                                },
                                                            );

                                                        return (
                                                            <div
                                                                className="container-event-single-location"
                                                                key={`${entry.show.location}-${entry.show.city}-${entry.project.title}`}
                                                            >
                                                                <h2>
                                                                    {
                                                                        entry
                                                                            .show
                                                                            .location
                                                                    }{" "}
                                                                    -{" "}
                                                                    {
                                                                        entry
                                                                            .show
                                                                            .city
                                                                    }
                                                                </h2>
                                                                <ul className="dates-container">
                                                                    {sortedDates.map(
                                                                        (
                                                                            date,
                                                                        ) => (
                                                                            <Event
                                                                                key={
                                                                                    date
                                                                                }
                                                                                date={
                                                                                    date
                                                                                }
                                                                                url={
                                                                                    entry
                                                                                        .show
                                                                                        .url
                                                                                }
                                                                                title={
                                                                                    entry
                                                                                        .project
                                                                                        .title
                                                                                }
                                                                            />
                                                                        ),
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        );
                                                    },
                                                )}
                                        </div>
                                    );
                                })}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default EventList;
